/**
 * PDP Cross-Sell Checkbox (new A/B-test block)
 *
 * Builds the colour dropdown for each cross-sell item that has more than one
 * variant (same colour-name → hex rules as bundle-multi-color.js), toggles
 * the "selected" card state, and — when at least one item is checked —
 * includes it in the next Add to Cart request for the main product.
 *
 * All selectors are scoped to `[data-pdp-cross-sell]` — deliberately not
 * `checkbox-cross-sell`, which is a separate, pre-existing Custom Liquid
 * cross-sell block already live on the store. Keep this prefix unique so
 * this script never queries/toggles that other block's markup (or vice
 * versa) if both happen to render on the same product page.
 *
 * Coordinates with bundle-multi-color.js via a shared `window.getExtraCartItems`
 * hook instead of racing it for the same click: if that script's own
 * multi-colour selector is active on the page, this script skips its own
 * interception for that click and only contributes the extra items — it's
 * bundle-multi-color.js's own doCartAdd() that reads the hook and POSTs them.
 * perfume-subscription-widget.js uses the same hook for the buttons it marks
 * with `data-psw-atc`.
 */
(function () {
  'use strict';

  // CSS colour for each variant option value — matches Figma design (same
  // mapping as assets/bundle-multi-color.js).
  var COLOR_MAP = {
    'Shiny Rose':    '#F1C9C9',
    'Soft black':    '#303538',
    'Soft Espresso': '#514242',
    'Sky Blue':      '#B9D9EA',
    'Lavender':      '#D6C7E8',
    'Sort':          '#1A1A1A',
    'Lyserød':       '#F4B8C6',
    'Hvid':          '#FFFFFF',
  };

  var SEL_ROOT          = '[data-pdp-cross-sell]';
  var SEL_ITEM          = '[data-pdp-cross-sell-item]';
  var SEL_OPTIONS       = '[data-pdp-cross-sell-options]';
  var SEL_DROPDOWN      = '[data-pdp-cross-sell-dropdown]';
  var SEL_SWATCH        = '[data-pdp-cross-sell-swatch]';
  var SEL_OPTION_LABEL  = '[data-pdp-cross-sell-option-label]';
  var SEL_PRICE         = '[data-pdp-cross-sell-price]';
  var SEL_PRODUCT_FORM  = 'form[id*="product-form"]';

  function colorForValue(value) {
    if (!value) return '#cccccc';
    if (COLOR_MAP[value]) return COLOR_MAP[value];
    var normalized = value.trim().toLowerCase();
    for (var key in COLOR_MAP) {
      if (key.toLowerCase() === normalized) return COLOR_MAP[key];
    }
    return '#cccccc';
  }

  function formatMoney(cents) {
    if (typeof window.formatMoney === 'function' && window.MinimogSettings) {
      return window.formatMoney(cents, window.MinimogSettings.money_format);
    }
    return (cents / 100).toFixed(2);
  }

  function boot() {
    var roots = document.querySelectorAll(SEL_ROOT);
    if (!roots.length) return;

    roots.forEach(function (root) {
      root.querySelectorAll(SEL_ITEM).forEach(initItem);
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest(SEL_OPTIONS) || e.target.closest(SEL_DROPDOWN)) return;
      roots.forEach(closeAllDropdowns);
    });

    attachCartIntercept(roots);
  }

  // ─── Per-item setup ───────────────────────────────────────────────────────
  function initItem(item) {
    // `item` is a <label for="..."> wrapping the whole card, so any click
    // that isn't cancelled by a descendant natively toggles the checkbox —
    // no manual click-to-toggle JS needed, and nothing on the page can break
    // it short of the label element itself being removed.
    var checkbox = item.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.addEventListener('change', function () {
        item.classList.toggle('is-selected', checkbox.checked);
      });
    }

    var optionsEl = item.querySelector(SEL_OPTIONS);
    if (!optionsEl) return;

    var dropdown = item.querySelector(SEL_DROPDOWN);

    // The colour pill/dropdown exists to change colour, not to select the
    // item — cancel the label's native forward-to-checkbox behaviour for any
    // click inside it, including its own padding (not just the option
    // buttons, which already preventDefault individually below).
    dropdown.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
    });

    var swatchEl = item.querySelector(SEL_SWATCH);
    var labelEl  = item.querySelector(SEL_OPTION_LABEL);
    if (swatchEl && labelEl) swatchEl.style.backgroundColor = colorForValue(labelEl.textContent.trim());

    var productId = optionsEl.dataset.productId;
    var script = document.querySelector('script[data-pdp-cross-sell-variants="' + productId + '"]');
    var variants = [];
    if (script) {
      try { variants = JSON.parse(script.textContent); } catch (e) { variants = []; }
    }

    variants.forEach(function (v) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-cross-sell__dropdown-option';
      if (String(v.id) === item.dataset.variantId) btn.classList.add('is-selected');
      if (!v.available) {
        btn.classList.add('is-unavailable');
        btn.disabled = true;
      }
      btn.dataset.variantId = v.id;

      var circle = document.createElement('span');
      circle.className = 'pdp-cross-sell__swatch';
      circle.style.backgroundColor = colorForValue(v.color);
      btn.appendChild(circle);
      btn.appendChild(document.createTextNode(v.color));

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!v.available) return;
        selectVariant(item, v, dropdown);
        closeAllDropdowns(item.closest(SEL_ROOT));
      });

      dropdown.appendChild(btn);
    });

    optionsEl.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var wasOpen = !dropdown.hidden;
      closeAllDropdowns(item.closest(SEL_ROOT));
      if (!wasOpen) {
        dropdown.hidden = false;
        optionsEl.setAttribute('aria-expanded', 'true');
      }
    });

    optionsEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        optionsEl.click();
      }
    });
  }

  function selectVariant(item, v, dropdown) {
    item.dataset.variantId = v.id;

    var swatchEl = item.querySelector(SEL_SWATCH);
    var labelEl  = item.querySelector(SEL_OPTION_LABEL);
    var priceEl  = item.querySelector(SEL_PRICE);
    var imgEl    = item.querySelector('.pdp-cross-sell__image img');
    var checkbox = item.querySelector('input[type="checkbox"]');

    if (swatchEl) swatchEl.style.backgroundColor = colorForValue(v.color);
    if (labelEl) labelEl.textContent = v.color;
    if (priceEl && v.price != null) priceEl.textContent = formatMoney(v.price);
    if (imgEl && v.image) imgEl.src = v.image;
    if (checkbox) checkbox.value = v.id;

    dropdown.querySelectorAll('.pdp-cross-sell__dropdown-option').forEach(function (opt) {
      opt.classList.toggle('is-selected', String(opt.dataset.variantId) === String(v.id));
    });
  }

  function closeAllDropdowns(root) {
    if (!root) return;
    root.querySelectorAll(SEL_DROPDOWN).forEach(function (d) { d.hidden = true; });
    root.querySelectorAll(SEL_OPTIONS).forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
  }

  // ─── Selected items → cart payload ────────────────────────────────────────
  function getSelectedItems(root) {
    var items = [];
    root.querySelectorAll(SEL_ITEM).forEach(function (item) {
      var checkbox = item.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        items.push({ id: item.dataset.variantId, quantity: 1 });
      }
    });
    return items;
  }

  // ─── Cart intercept ───────────────────────────────────────────────────────
  // Exposed so other add-to-cart flows (e.g. bundle-multi-color.js's own
  // multi-colour POST) can fold these items into their own request instead
  // of two scripts racing to intercept the same click.
  function getAllSelectedItems(roots) {
    var items = [];
    roots.forEach(function (root) {
      items = items.concat(getSelectedItems(root));
    });
    return items;
  }

  function resetSelection(roots) {
    roots.forEach(function (root) {
      root.querySelectorAll('input[type="checkbox"]').forEach(function (checkbox) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change'));
      });
    });
  }

  function attachCartIntercept(roots) {
    window.getExtraCartItems = function () {
      return getAllSelectedItems(roots);
    };
    // Called by whichever script actually performed the /cart/add.js request
    // (this one, or bundle-multi-color.js when it owned the click) so the
    // cards reset either way.
    window.resetExtraCartItems = function () {
      resetSelection(roots);
    };

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="add"], button[type="submit"].add-to-cart');
      if (!btn) return;

      var selectedItems = getAllSelectedItems(roots);
      if (!selectedItems.length) return;

      // bundle-multi-color.js owns the click (and already reads
      // window.getExtraCartItems) whenever its own multi-colour selector is
      // showing — don't also intercept here, or the two would race.
      var bcWrap = document.querySelector('.bc-wrap');
      if (bcWrap && getComputedStyle(bcWrap).display !== 'none') return;

      // Same for perfume-subscription-widget.js: it builds the real line items
      // (2-pack slots, selling plan) that the form's single `id` can't express.
      if (btn.hasAttribute('data-psw-atc')) return;

      var form = btn.closest(SEL_PRODUCT_FORM);
      if (!form) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      doCartAdd(form, btn, selectedItems, roots);
    }, true);
  }

  function doCartAdd(form, submitBtn, extraItems, roots) {
    var origText = submitBtn.textContent;
    submitBtn.disabled = true;

    var formData = new FormData(form);
    var items = [{
      id: formData.get('id'),
      quantity: parseInt(formData.get('quantity') || 1, 10),
    }].concat(extraItems);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) return res.text().then(function (t) { throw new Error(t); });
        return res.json();
      })
      .then(function (data) {
        submitBtn.disabled = false;
        submitBtn.textContent = origText;
        triggerCartUpdate(data);
        resetSelection(roots);
      })
      .catch(function (err) {
        console.error('[pdp-cross-sell] Cart error:', err.message);
        submitBtn.disabled = false;
        submitBtn.textContent = origText;
        form.submit();
      });
  }

  function triggerCartUpdate(data) {
    if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
      var lineItem = (data && data.items && data.items[0]) ? data.items[0] : data;
      window.Shopify.onItemAdded(lineItem);
      return;
    }
    if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
      window.MinimogEvents.emit('ON_ITEM_ADDED', data);
    }
  }

  // ─── Run ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
